import {useEffect, useMemo, useState} from "react";
import useAuthStore from "../../stores/authStore";
import {DELIVERY_FEE, effectivePrice} from "./marketplaceApi";

// Marketplace cart, kept per merchant account in localStorage so it survives a
// reload. Lines: [{product, quantity}].
const storageKey = (accountId) => `marketplaceCart_${accountId || "default"}`;

const readCart = (accountId) => {
    try {
        return JSON.parse(localStorage.getItem(storageKey(accountId))) || [];
    } catch {
        return [];
    }
};

const useMarketplaceCart = () => {
    const accountId = useAuthStore((state) => state.selectedAccount?.id);
    // The account travels with its lines so switching account never writes one
    // account's cart under the other's key.
    const [cart, setCart] = useState(() => ({accountId, lines: readCart(accountId)}));
    if (cart.accountId !== accountId) setCart({accountId, lines: readCart(accountId)});
    const {lines} = cart;
    const setLines = (update) => setCart((c) => ({...c, lines: update(c.lines)}));

    useEffect(() => {
        try {
            localStorage.setItem(storageKey(cart.accountId), JSON.stringify(cart.lines));
        } catch {
            // storage unavailable: the cart just lives for this session
        }
    }, [cart]);

    const add = (product, quantity = 1) =>
        setLines((current) => {
            const existing = current.find((l) => l.product.id === product.id);
            if (existing) {
                return current.map((l) => l.product.id === product.id ? {...l, quantity: l.quantity + quantity} : l);
            }
            return [...current, {product, quantity}];
        });

    const setQuantity = (productId, quantity) =>
        setLines((current) => quantity <= 0
            ? current.filter((l) => l.product.id !== productId)
            : current.map((l) => l.product.id === productId ? {...l, quantity} : l));

    const remove = (productId) => setLines((current) => current.filter((l) => l.product.id !== productId));
    const clear = () => setLines(() => []);

    const totals = useMemo(() => {
        const subtotal = lines.reduce((sum, l) => sum + l.product.price * l.quantity, 0);
        const itemsTotal = lines.reduce((sum, l) => sum + effectivePrice(l.product) * l.quantity, 0);
        const deliveryFee = lines.length > 0 ? DELIVERY_FEE : 0;
        return {
            count: lines.reduce((sum, l) => sum + l.quantity, 0),
            subtotal,
            discount: subtotal - itemsTotal,
            deliveryFee,
            // To pay: discounted lines + delivery
            total: itemsTotal + deliveryFee,
        };
    }, [lines]);

    return {lines, add, setQuantity, remove, clear, ...totals};
};

export default useMarketplaceCart;
